import React, { useState } from 'react';
import { Section } from './Section';
import { Clock, Shield, Lock, Zap, ShieldCheck, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
interface PricingProps {
  timeLeft: number;
}
type PlanKey = 'standard' | 'pro';
const plans = {
  standard: {
    emoji: '📘',
    name: 'STRETCHING RESUELTO',
    description: '6 Tomos + 6 Bonos',
    originalPrice: '$34.990',
    price: '$19.990',
    url: 'https://pagos.recursosfitness.shop/cart/44003506061409:1',
    highlight: false,
    badges: [],
    savingsTag: null
  },
  pro: {
    emoji: '🏆',
    name: 'STRETCHING RESUELTO + CUERPO SEGURO PRO™',
    description: '6 Tomos + 6 Bonos + Cuerpo Seguro Pro™',
    originalPrice: '$43.990',
    price: '$27.990',
    url: 'https://pagos.recursosfitness.shop/cart/44003506651233:1',
    highlight: true,
    badges: ['⭐ MÁS ELEGIDO', 'AHORRÁS $16.000'],
    savingsTag: 'AHORRÁS $16.000'
  }
};
export function Pricing({
  timeLeft
}: PricingProps) {
  const [selected, setSelected] = useState<PlanKey>('pro');
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')} HORAS : ${m.toString().padStart(2, '0')} MIN : ${s.toString().padStart(2, '0')} SEG`;
  };
  const handleBuy = () => {
    window.open(plans[selected].url, '_blank');
  };
  return <Section id="pricing" className="bg-brand-dark" data-id="element-228">
      <div className="max-w-2xl mx-auto" data-id="element-229">
        {/* Urgency Bar */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 md:p-6 text-center mb-8" data-id="element-230">
          <h3 className="text-red-400 font-bold tracking-widest text-xs mb-2" data-id="element-231">
            EL PRECIO DE LANZAMIENTO TERMINA EN:
          </h3>
          <div className="sm:text-3xl md:text-5xl font-display font-black text-white mb-2 tabular-nums text-[20px]" data-id="element-232">
            {formatTime(timeLeft)}
          </div>
          <p className="text-gray-300 text-sm mb-4" data-id="element-233">
            Después sube $8.000 ARS y no vuelve a bajar.
          </p>

          <div className="max-w-md mx-auto" data-id="element-234">
            <div className="flex justify-between text-xs text-gray-400 mb-1.5 font-medium" data-id="element-235">
              <span className="flex items-center" data-id="element-236">
                <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5 animate-pulse" data-id="element-237"></span>
                Cupos al precio de lanzamiento
              </span>
              <span data-id="element-238">68% tomados</span>
            </div>
            <div className="h-2.5 bg-brand-light rounded-full overflow-hidden" data-id="element-239">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '68%'
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full" data-id="element-240"></motion.div>
            </div>
          </div>
        </div>

        <div className="text-center mb-6" data-id="element-241">
          <h2 className="text-2xl md:text-4xl font-display font-black text-white mb-2" data-id="element-242">
            Elegí tu Stretching Resuelto:
          </h2>
          <p className="text-brand-accent text-sm md:text-lg" data-id="element-243">
            Las dos opciones incluyen los 6 bonos estratégicos completos
          </p>
        </div>

        {/* Plan Options — Radio Select Style */}
        <div className="space-y-3 mb-6" data-id="element-244">
          {/* Standard Option */}
          <button onClick={() => setSelected('standard')} className={`w-full text-left rounded-xl p-4 md:p-5 transition-all duration-200 border-2 ${selected === 'standard' ? 'border-brand-accent bg-brand-light/60' : 'border-white/10 bg-brand-light/30 hover:border-white/20'}`} data-id="element-245">
            <div className="flex items-center gap-3 md:gap-4" data-id="element-246">
              {/* Radio circle */}
              <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${selected === 'standard' ? 'border-brand-accent' : 'border-gray-500'}`} data-id="element-247">
                {selected === 'standard' && <div className="w-2.5 h-2.5 rounded-full bg-brand-accent" data-id="element-248"></div>}
              </div>

              <div className="flex-1 min-w-0" data-id="element-249">
                <div className="flex items-center gap-2 mb-0.5" data-id="element-250">
                  <span className="text-lg" data-id="element-251">📘</span>
                  <h3 className="text-sm md:text-base font-display font-bold text-white" data-id="element-252">
                    STRETCHING RESUELTO
                  </h3>
                </div>
                <p className="text-gray-400 text-xs md:text-sm" data-id="element-253">
                  6 Tomos + 6 Bonos
                </p>
              </div>

              <div className="text-right shrink-0" data-id="element-254">
                <div className="text-gray-500 line-through text-xs md:text-sm" data-id="element-255">
                  $34.990
                </div>
                <div className="text-2xl md:text-3xl font-display font-black text-white" data-id="element-256">
                  $19.990
                </div>
              </div>
            </div>
          </button>

          {/* Pro Option — Highlighted */}
          <button onClick={() => setSelected('pro')} className={`w-full text-left rounded-xl p-4 md:p-5 transition-all duration-200 border-2 relative ${selected === 'pro' ? 'border-brand-accent bg-brand-accent/5 shadow-[0_0_24px_rgba(45,212,191,0.12)]' : 'border-brand-accent/40 bg-brand-light/30 hover:border-brand-accent/60'}`} data-id="element-257">
            <div className="flex items-center gap-3 md:gap-4" data-id="element-258">
              {/* Radio circle */}
              <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${selected === 'pro' ? 'border-brand-accent' : 'border-gray-500'}`} data-id="element-259">
                {selected === 'pro' && <div className="w-2.5 h-2.5 rounded-full bg-brand-accent" data-id="element-260"></div>}
              </div>

              <div className="flex-1 min-w-0" data-id="element-261">
                <div className="flex items-center gap-2 mb-1" data-id="element-262">
                  <span className="text-lg" data-id="element-263">🏆</span>
                  <h3 className="text-sm md:text-base font-display font-bold text-white" data-id="element-264">
                    PACK COMPLETO
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-1" data-id="element-265">
                  <span className="text-[10px] md:text-xs font-bold bg-brand-accent text-brand-dark px-2 py-0.5 rounded-md" data-id="element-266">
                    ⭐ MÁS ELEGIDO
                  </span>
                  <span className="text-[10px] md:text-xs font-bold bg-brand-accent/15 text-brand-accent px-2 py-0.5 rounded-md border border-brand-accent/30" data-id="element-267">
                    AHORRÁS $16.000
                  </span>
                </div>
                <p className="text-gray-400 text-xs md:text-sm" data-id="element-268">
                  6 Tomos + 6 Bonos + Cuerpo Seguro Pro™
                </p>
              </div>

              <div className="text-right shrink-0" data-id="element-269">
                <div className="text-gray-500 line-through text-xs md:text-sm" data-id="element-270">
                  $43.990
                </div>
                <div className="text-2xl md:text-3xl font-display font-black text-brand-accent" data-id="element-271">
                  $27.990
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* CTA Button */}
        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={handleBuy} className="w-full bg-brand-accent hover:bg-brand-accentHover text-brand-dark font-black text-base md:text-xl py-4 md:py-5 rounded-xl shadow-[0_0_30px_rgba(45,212,191,0.3)] hover:shadow-[0_0_40px_rgba(45,212,191,0.5)] transition-all mb-3" data-id="element-272">
          SÍ, QUIERO MI STRETCHING RESUELTO →
        </motion.button>

        <p className="text-xs md:text-sm text-gray-400 text-center italic mb-8" data-id="element-273">
          {selected === 'pro' ? 'La más elegida por profes que quieren enseñar con seguridad total' : 'Todo tu año de Stretching resuelto con clases listas para usar'}
        </p>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-gray-400 text-xs md:text-sm font-medium border-t border-white/5 pt-5" data-id="element-274">
          <div className="flex items-center" data-id="element-275">
            <Lock className="w-4 h-4 mr-1.5 text-brand-accent" data-id="element-276" /> Pago seguro
          </div>
          <div className="flex items-center" data-id="element-277">
            <Zap className="w-4 h-4 mr-1.5 text-brand-accent" data-id="element-278" /> Acceso en 5 min
          </div>
          <div className="flex items-center" data-id="element-279">
            <ShieldCheck className="w-4 h-4 mr-1.5 text-brand-accent" data-id="element-280" />{' '}
            Garantía 7 días
          </div>
          <div className="flex items-center font-bold text-white" data-id="element-281">
            <CreditCard className="w-4 h-4 mr-1.5 text-brand-accent" data-id="element-282" /> Mercado
            Pago
          </div>
        </div>
      </div>
    </Section>;
}